import React, { useState } from 'react';
import { MenuItem, menuApi } from '../api/client';

interface Props {
  restaurantId: string;
  item?: MenuItem;            // undefined = create mode
  categories: string[];
  onSave: (item: MenuItem) => void;
  onClose: () => void;
}

export const MenuItemModal: React.FC<Props> = ({ restaurantId, item, categories, onSave, onClose }) => {
  const isEdit = !!item;

  const [form, setForm] = useState({
    name: item?.name || '',
    category: item?.category || categories[0] || '',
    price: item?.price?.toString() || '',
    description: item?.description || '',
    calories: item?.calories?.toString() || '',
    tags: item?.tags?.join(', ') || '',
    imageUrl: item?.imageUrl || '',
    isAvailable: item?.isAvailable !== false,
    sortOrder: item?.sortOrder?.toString() || '99',
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>(item?.imageUrl || '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (field: string, value: string | boolean) =>
    setForm((f) => ({ ...f, [field]: value }));

  const handleImagePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.category || !form.price) {
      setError('Name, category, and price are required.');
      return;
    }
    setError('');
    setSaving(true);

    try {
      let finalImageUrl = form.imageUrl;

      // Upload new image if selected
      if (imageFile) {
        setUploading(true);
        const { uploadUrl, imageUrl: s3Url } = await menuApi.getUploadUrl(
          restaurantId, imageFile.name, imageFile.type
        );
        await menuApi.uploadImageToS3(uploadUrl, imageFile);
        finalImageUrl = s3Url;
        setUploading(false);
      }

      const payload = {
        name: form.name.trim(),
        category: form.category,
        price: parseFloat(form.price),
        description: form.description.trim(),
        calories: form.calories ? parseInt(form.calories) : undefined,
        tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        imageUrl: finalImageUrl,
        isAvailable: form.isAvailable,
        sortOrder: parseInt(form.sortOrder) || 99,
      };

      let saved: MenuItem;
      if (isEdit) {
        saved = await menuApi.update(restaurantId, item!.itemId, payload);
      } else {
        saved = await menuApi.create(restaurantId, payload);
      }

      onSave(saved);
    } catch (err) {
      setError('Failed to save item. Please try again.');
      console.error(err);
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
      <div className="bg-gray-900 rounded-2xl border border-white/10 w-full max-w-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-lg font-bold text-white">{isEdit ? 'Edit Item' : 'New Menu Item'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl leading-none">×</button>
        </div>

        <div className="p-6 space-y-5">
          {/* Image upload */}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Item Image</label>
            <div
              className="relative w-full h-40 rounded-xl border-2 border-dashed border-white/10 flex items-center justify-center cursor-pointer overflow-hidden group hover:border-indigo-500 transition-colors"
              onClick={() => document.getElementById('img-input')?.click()}
              style={imagePreview ? { backgroundImage: `url(${imagePreview})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
            >
              {!imagePreview && (
                <div className="text-center">
                  <div className="text-4xl mb-2">📷</div>
                  <p className="text-gray-500 text-sm">Click to upload image</p>
                  <p className="text-gray-600 text-xs">JPG, PNG, WebP, GIF</p>
                </div>
              )}
              {imagePreview && (
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="text-white text-sm font-semibold">Click to change</span>
                </div>
              )}
              <input id="img-input" type="file" accept="image/*" className="hidden" onChange={handleImagePick} />
            </div>
            {uploading && <p className="text-indigo-400 text-sm mt-1">Uploading image…</p>}
          </div>

          {/* Name */}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Name *</label>
            <input
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500"
              value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Classic Smash Burger"
            />
          </div>

          {/* Category + Price row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Category *</label>
              <select
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500"
                value={form.category} onChange={(e) => set('category', e.target.value)}
              >
                {categories.map((c) => <option key={c} value={c} className="bg-gray-900">{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Price *</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <input
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-8 pr-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500"
                  type="number" step="0.01" min="0" value={form.price}
                  onChange={(e) => set('price', e.target.value)} placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Description</label>
            <textarea
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500 resize-none"
              rows={2} value={form.description} onChange={(e) => set('description', e.target.value)}
              placeholder="Short description of the item"
            />
          </div>

          {/* Calories + Sort Order */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Calories</label>
              <input
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500"
                type="number" value={form.calories} onChange={(e) => set('calories', e.target.value)} placeholder="e.g. 650"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Sort Order</label>
              <input
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500"
                type="number" value={form.sortOrder} onChange={(e) => set('sortOrder', e.target.value)} placeholder="1–99"
              />
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Tags <span className="text-gray-600 normal-case">(comma separated)</span></label>
            <input
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500"
              value={form.tags} onChange={(e) => set('tags', e.target.value)} placeholder="e.g. popular, spicy, vegan"
            />
          </div>

          {/* Available toggle */}
          <div className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-3">
            <div>
              <p className="text-white font-semibold">Available on Kiosk</p>
              <p className="text-gray-500 text-xs">Turn off to hide from the ordering screen</p>
            </div>
            <button
              onClick={() => set('isAvailable', !form.isAvailable)}
              className={`w-12 h-6 rounded-full transition-colors ${form.isAvailable ? 'bg-indigo-500' : 'bg-gray-700'}`}
            >
              <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform mx-0.5 ${form.isAvailable ? 'translate-x-6' : 'translate-x-0'}`} />
            </button>
          </div>

          {error && <p className="text-red-400 text-sm bg-red-500/10 rounded-xl px-4 py-3">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-white/10">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-white/10 text-gray-400 hover:text-white hover:border-white/30 transition-colors font-semibold">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white font-bold transition-colors"
          >
            {saving ? (uploading ? 'Uploading…' : 'Saving…') : (isEdit ? 'Save Changes' : 'Add Item')}
          </button>
        </div>
      </div>
    </div>
  );
};
